import {
  getPagePath,
  useSoyuzData,
  useSoyuzLocales,
  useSoyuzServer,
  useSoyuzRequest,
  trans
} from '@spotahome/soyuz/client';

import {
  ThemeProvider,
  CookiesProvider,
  ToastContainer,
  GoogleOneTap,
  UserInfoProvider,
  HotspotsProvider,
  DeviceProvider,
  FooterSimplified,
  AppBanner,
  HeaderContainer
} from '@spotahome/ui-library';
import useCoreWebVitals from '@spotahome/ui-library/src/hooks/useCoreWebVitals';
import usePageLoadTime from '@spotahome/ui-library/src/hooks/usePageLoadTime';
import ZohoChatbot from '@spotahome/ui-library/src/tenant/components/ZohoChatbot/ZohoChatbot';
import LandingTestimonialsSection from '@spotahome/ui-library/src/tenant/components/LandingTestimonialsSection';
import { SectionNavigationProvider } from '@spotahome/ui-library/src/tenant/context/SectionNavigationContext';

import NotFound from '@spotahome/seo-landings-common/src/components/NotFound';
import SeoLocalesContainer from '@spotahome/seo-landings-common/src/components/SeoLocalesContainer';

import '@spotahome/seo-landings-common/src/scss/init.scss';

import LandingFaqsSection from '@spotahome/ui-library/src/tenant/components/LandingFaqsSection';

import Hero from './components/Hero';
import CountryInformation from './components/CountryInformation';
import CountryContentNavigation from './components/CountryContentNavigation';
import Cities from './components/Cities';
import TopCities from './components/TopCities';
import AllCities from './components/AllCities';
import Blog from './components/Blog';

import './main.scss';
import SellingPoints from './components/SellingPoints';
import { getCountryId, COUNTRY_NAMES_BY_LOCALE } from './config';
import { getFaqs } from './utils/faqs';

const buildRouting = (currentRoute, countryParam, availableLocales) => {
  const countryId = getCountryId(countryParam);
  if (!countryId) {
    return {};
  }

  const countryNamesByLocale = COUNTRY_NAMES_BY_LOCALE[countryId];
  if (!countryNamesByLocale) {
    return {};
  }

  return availableLocales.reduce((acc, locale) => {
    const countryName = countryNamesByLocale[locale];
    if (countryName) {
      acc[locale] = getPagePath(currentRoute, {
        locale,
        params: {
          country: countryName
        }
      });
    }
    return acc;
  }, {});
};

const Countries = () => {
  const { currentRoute } = useSoyuzServer();
  const { routes } = useSoyuzLocales();
  const { params } = useSoyuzRequest();
  const availableLocales = Object.keys(routes);
  const { [Countries.NAME]: requestedData } = useSoyuzData();

  const { error, userId, initialCookie, deviceType, countryConfig } =
    requestedData;

  const routing = buildRouting(currentRoute, params.country, availableLocales);

  useCoreWebVitals();
  usePageLoadTime();

  if (error === 'notFound' || error === 'gone') {
    return (
      <NotFound
        initialCookie={initialCookie}
        deviceType={deviceType}
        error={error}
      />
    );
  }

  return (
    <CookiesProvider initialCookie={initialCookie}>
      <ThemeProvider flags={{ newButtons: true }}>
        <UserInfoProvider>
          <HotspotsProvider>
            <DeviceProvider deviceType={deviceType}>
              <SectionNavigationProvider>
                <SeoLocalesContainer routing={routing}>
                  <ToastContainer />
                  {!userId && <GoogleOneTap />}
                  <ZohoChatbot />
                  <AppBanner source="seo" deviceType={deviceType} />
                  <HeaderContainer showLogin />
                  <Hero countryName={countryConfig.countryName} />
                  <CountryInformation
                    countryId={countryConfig.countryId}
                    countryName={countryConfig.countryName}
                  />
                  {/* Wrapper for the section to be on top */}
                  <div style={{ position: 'relative' }}>
                    <CountryContentNavigation />
                    <TopCities
                      countryId={countryConfig.countryId}
                      countryName={countryConfig.countryName}
                      cities={countryConfig.cities}
                    />
                    <AllCities cities={countryConfig.cities} />
                    <Cities
                      countryId={countryConfig.countryId}
                      cities={countryConfig.cities}
                    />
                    <Blog
                      mainPosts={countryConfig.blog.mainPosts}
                      otherPosts={countryConfig.blog.otherPosts}
                      countryName={countryConfig.countryName}
                    />
                    <SellingPoints countryName={countryConfig.countryName} />
                    <LandingTestimonialsSection
                      backgroundColor="secondary-blue-light"
                      observableSection="testimonials"
                    />
                    <LandingFaqsSection
                      faqs={getFaqs(countryConfig.countryId, trans)}
                      backgroundColor="white"
                      observableSection="faqs"
                    />
                  </div>
                  <FooterSimplified />
                </SeoLocalesContainer>
              </SectionNavigationProvider>
            </DeviceProvider>
          </HotspotsProvider>
        </UserInfoProvider>
      </ThemeProvider>
    </CookiesProvider>
  );
};

Countries.NAME = 'Countries';

export default Countries;
